import { createContext, useContext } from 'react';
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AgTechQuery, AgTechQueryResponses } from './queries/AgTechDataQueries';

/// Operation Entities

export type AgTechOperation = {
    operationId: number,
    operationName: string,
    operationHostName: string,
    operationLogo?: string
}

export type AgTechOperationApplicationStatus = {
    isProvisioned: boolean,
    isSetupComplete: boolean,
    termsOfServiceVersion: string,
    acceptedTermsOfServiceVersion: string
}

export const DefaultAgTechOperation: AgTechOperation = {
    operationId: 0,
    operationName: '',
    operationHostName: ''
};

/// Operation Queries

// Default operation status query returns a fully provisioned and setup operation for apps that do not
// require operation provisionment or setup
export const AgTechOperationDefaultStatusQuery: AgTechQuery<any, AgTechOperationApplicationStatus> = {
    queryKey: () => ['OperationStatus'],
    query: async () => {
        return AgTechQueryResponses.Success({
            isProvisioned: true,
            isSetupComplete: true,
            termsOfServiceVersion: '',
            acceptedTermsOfServiceVersion: ''
        });
    },
    content: {
        loadingText: 'Loading operation status...',
        failureText: 'Failed to load operation status.'
    }
}

/// Operation Slices

const operationDataSlice = createSlice({
    name: 'operation',
    initialState: DefaultAgTechOperation,
    reducers: {
        setOperation: (state, action: PayloadAction<AgTechOperation>) => {
            return {
                ...state,
                ...action.payload
            }
        }
    }
});

/// Operation Actions

export type AgTechOperationDataActions = {
    setOperation: () => Promise<void>
}

/// Operation Context

export type AgTechOperationContext = {
    operation: AgTechOperation
}

export const OperationContext = createContext<AgTechOperationContext>({
    operation: DefaultAgTechOperation
});

/// Operation Hooks

export const useOperationData = (): AgTechOperation => {
    return useContext(OperationContext).operation;
}

export default operationDataSlice.reducer;